"use client";

import { IPageContent } from "api/interfaces/static-content.type";
import classNames from "classnames";
import StaticBredcrumList from "component/breadcrumb/StaticBredcrumList";
import BreadcrumbItem from "component/breadcrumb/breadcrumb-item.component";
import CategoryViewItem from "component/categories-viewer/CategoryViewItem";
import PAText from "component/design-system/typography/PAText/PAText";
import useSafeRouter from "hoc/useSafeRouter";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import QS from "query-string";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { isValidCategory } from "util/category/is-valid-category";
import { deepFind } from "util/helper/deepFind";
import { encode } from "util/helper/encode";
import { capitalize } from "util/lodash/capitalize";
import { PRODUCT_PAGE_TYPES } from "util/product/product-constants";
import { b2bUrl } from "util/url/static-urls";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { ArrowLeft } from "lucide-react";
import VerticalSeparator from "component/product/vertical-separator/VerticalSeparator";
import styles from "./breadcrumb.module.scss";

const BreadcrumbDropdownItem = dynamic(() => import(/* webpackChunkName: "breadcrumb-dropdown-item" */ "component/breadcrumb/breadcrumb-dropdown-item.component"));

interface IProps {
  staticContent: IPageContent;
}

function Breadcrumbs(props: IProps) {
  const {
    category, primaryCategory, breadCrumbsV2: oldBreadcrumbs,
    breadcrumbs, staticContent, showBackToResult, className = "",
    bannerData, partNumber, openGuaranteedFitProductOverlay,
  } = props;
  const breadCrumbsV2 = oldBreadcrumbs || [breadcrumbs];
  const breadcrumbCategory = isValidCategory(primaryCategory) ? primaryCategory : category;

  const breadcrumbRef = useRef();
  const router = useRouter();
  const { query } = useSafeRouter();
  const { t } = useTranslation(["common"]);

  // todo remove staticContent, t0, t1, t2, t3 and pass category object as props.
  const { pageDetails = {} } = staticContent || {};
  const singleBredCRumList = breadCrumbsV2[0] || [];
  const tier0 = breadcrumbCategory?.tier0 || pageDetails.tier0 || "";
  const tier1 = breadcrumbCategory?.tier1 || pageDetails.tier1 || "";
  const tier2 = breadcrumbCategory?.tier2 || pageDetails.tier2 || "";
  const tier3 = breadcrumbCategory?.tier3 || pageDetails.tier3;

  const pageDisplayableTitle = deepFind(pageDetails, "pageDisplayableTitle", bannerData?.categoryDisplayableTitle);
  const { pathname } = query;

  const { width } = useSelector((store: RootState) => store.utils);
  const currentPageType = useSelector((state: RootState) => state.utils.currentPageType);
  const { selectedVehicle } = useSelector((state: RootState) => state.garage);
  const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);

  const formatTitle = (title: any, _encode: any) => encode(title, _encode, " ");
  let productPageLastBreadCrumb: any = null;

  useEffect(() => {
    if (breadcrumbRef.current) {
      // raybestos-front-disc-brake-rotor-96711r -> To Show last breadcrumb in the viewport
      // scrolling to the last breadcrumb
      breadcrumbRef.current.scrollLeft = breadcrumbRef.current.scrollWidth - breadcrumbRef.current.clientWidth;
    }
  }, [width]);

  if (PRODUCT_PAGE_TYPES.includes(currentPageType)) {
    productPageLastBreadCrumb = (
      <BreadcrumbItem
        title={`${t("Part")}# ${partNumber}`}
        isLast
      />
    );
  }

  let currentPageBreadcrum;
  let filtersBreadCrums = [];

  let finalLastBreadcrumsUI: any = [];
  if (pageDisplayableTitle) {
    currentPageBreadcrum = { title: pageDisplayableTitle, url: `/${pathname}` };
    filtersBreadCrums = [query.tier1, query.tier2, query.tier3]
      .filter(Boolean)
      .map((_) => ({
        title: capitalize(_),
        url: `/${pathname}?${QS.stringify({
          tier1: query.tier1,
          tier2: query.tier2,
          // we don't add T3 since, remove URL from last item
          // tier3: index <= 2 ? query.tier3 : undefined,
        })}`,
      }));
    const finalLastBreadcrums = [currentPageBreadcrum, ...filtersBreadCrums];
    finalLastBreadcrumsUI = finalLastBreadcrums
      .map((item, index) => (
        <BreadcrumbItem
          key={`${item.title} ${index}`}
          {...item}
          // remove url from last BreadcrumLi
          url={index === finalLastBreadcrums.length - 1 ? null : item.url}
          className={styles.breadcrumb_item}
          isLast={!productPageLastBreadCrumb && index === finalLastBreadcrums.length - 1}
          showSeparator
        />
      ));
  }

  const mainBreadcrumList = singleBredCRumList.map((_: any, index: any) => {
    const { pageType, title, url } = _;
    const formatedTitle = formatTitle(title, "(-|_)");

    const TierCategoryView = ({
      isTier3,
    }: any) => {
      let categoryObj = { tier0, tier1, tier2 };
      if (pageType === "TIER0_PAGE") {
        categoryObj = { tier0 };
      } else if (pageType === "TIER1_PAGE") {
        categoryObj = { tier0, tier1 };
      } else if (isTier3) {
        categoryObj.tier3 = tier3;
      }

      const breadCrumbItem = (
        <BreadcrumbItem
          isLast={!productPageLastBreadCrumb && !pageDisplayableTitle && index === singleBredCRumList.length - 1}
          title={formatedTitle}
          className={classNames(styles.breadcrumb_item, {
            [styles.tier3_breadcrumb]: PRODUCT_PAGE_TYPES.includes(currentPageType) && isTier3 && openGuaranteedFitProductOverlay,
          })}
          showSeparator
          url={url}
          categoryObj={categoryObj}
          key={`${pageType} ${index}`}
        />
      );

      if (!validateVehicle(selectedVehicle) && url) {
        return breadCrumbItem;
      }

      return (

        <CategoryViewItem
          url={url}
          passOnClick
          categoryObj={categoryObj}
          defaultFilters={{
            manufacturers: [],
            prices: [],
            fitments: [],
            availabilities: [],
          }}
          key={`${pageType} ${index}`}
        >

          {breadCrumbItem}
        </CategoryViewItem>
      );
    };

    switch (pageType) {
    case "TIER0_PAGE":
    case "TIER1_PAGE":
    case "TIER2_PAGE":
    case "TIER2_MAKE_PAGE": // ford-focus-exhaust-mufflers
    case "MAKE_MODEL_PAGE":
    case "TIER2_MAKE_MODEL_PAGE": // ?
    case "ALL_MAKES_PAGE":
    case "MAKE_PAGE":
    case "ALL_MANUFACTURERS_PAGE":
      return (
        <TierCategoryView key={`${pageType} ${index}`} />
      );

    case "TIER3_PAGE":
    case "TIER3_MAKE_PAGE": // ford-focus-rear-brake-shoe
    case "TIER3_MAKE_MODEL_PAGE": // 1995-ford-ranger-oil-filter-transit-warehouse-lop-lp3600
      return (
        <TierCategoryView
          isTier3
          key={`${pageType} ${index}`}
        />
      );

    case "MANUFACTURER_PAGE":
      return (
        <BreadcrumbItem
          title={formatedTitle}
          url={url}
          showSeparator
          className={styles.breadcrumb_item}
          isLast={!productPageLastBreadCrumb && !pageDisplayableTitle && index === singleBredCRumList.length - 1}
          key={`${pageType} ${index}`}
        />
      );
    case "STATIC_PAGE":
    case "LANDING_PAGE":
      return (
        <BreadcrumbItem
          title={formatedTitle}
          url={url}
          key={`${pageType} ${index}`}
        />
      );
    case "ARTICLES":
      return (
        <BreadcrumbDropdownItem
          id="breadcrumb_t2"
          className="tier2"
          title={formatedTitle}
          key={`${pageType} ${index}`}
        >
          <StaticBredcrumList />
        </BreadcrumbDropdownItem>
      );
    default:
      return formatedTitle;
    }
  });

  const firstBreadcrum = (
    <BreadcrumbItem
      id="breadcrumb_t0"
      title={t("Home")}
      url={businessCustomer ? b2bUrl : "/"}
      className={styles.breadcrumb_item}
      showSeparator={!((!mainBreadcrumList || mainBreadcrumList.length === 0)
        && (!finalLastBreadcrumsUI || finalLastBreadcrumsUI.length === 0) && (!productPageLastBreadCrumb))}
    />
  );

  return (
    <div className={`${styles.breadcrumb_container} ${className}`} ref={breadcrumbRef}>

      <ul
        className={classNames({
          [styles.breadCrumb]: true,
        }, "breadcrumb-category")}
      >
        {showBackToResult && (
          <div className="d-flex align-items-center">
            <ArrowLeft size={14} strokeWidth={5} color="#1a4d7c" />
            &nbsp;
            <PAText component="span" onClick={router.back} className={styles.back_button}>
              {t("Back to Results")}
            </PAText>
            <VerticalSeparator className={styles.vertical_separator} />
          </div>

        )}
        {firstBreadcrum}
        {mainBreadcrumList}
        {finalLastBreadcrumsUI}
        {productPageLastBreadCrumb}
      </ul>

    </div>
  );
}

export default Breadcrumbs;
